import { useCallback } from "react";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { CachedFetch, useCachedFetch } from "@shared/hooks/useCachedFetch";
import { getSiteLatencyMatrixAPI, SiteLatencyMatrixData } from "./request";

/**
 * The key carries no tenant on purpose: the matrix is backbone-wide and the
 * backend answers every allowed caller with the same cached payload, so the
 * Monitoring tab and the Overview map share one answer.
 */
const SITE_LATENCY_MATRIX_KEY = "kontroledge/monitoring/site-latency-matrix";

/**
 * Whether the backend serves the matrix to this caller: its route sits behind
 * `partnerAccessControl`, which lets PARTNER and GLOBAL_ADMIN tenants through
 * whatever customer they have selected, and refuses everyone else with a 403.
 */
export function canReadSiteLatency(tenantType: string | null | undefined): boolean {
    return tenantType === "PARTNER" || tenantType === "GLOBAL_ADMIN";
}

/**
 * RTT between backbone sites (CTX-842), shared by Network monitoring →
 * Latency and the Overview map (CTX-843). `enabled` false asks nothing: the
 * endpoint is partner-gated, and a caller it would refuse should not try.
 */
export function useSiteLatencyMatrix(
    enabled = true
): CachedFetch<SiteLatencyMatrixData> {
    const api = useAxiosAuth0();
    const load = useCallback(async () => {
        const response = await getSiteLatencyMatrixAPI(api);
        if (!response.data.data) {
            throw new Error(response.data.message || "No latency data");
        }
        return response.data.data;
    }, [api]);
    return useCachedFetch(enabled ? SITE_LATENCY_MATRIX_KEY : null, load);
}
